import { getTimeBreakdownStrings } from '@automata/ui/src/DurationPickerV2/utils';
import { logger } from '@automata/utils';
import intersection from 'lodash/intersection';
import union from 'lodash/union';
import uniq from 'lodash/uniq';
import type { Connection, EdgeChange, NodeChange } from 'reactflow';
import { getConnectedEdges } from 'reactflow';
import { UAParser } from 'ua-parser-js';

import { getFormModesFromProps } from './dataModes';
import { parseHandlePairType } from './graph';
import {
  DATA_HANDLE_IDENTIFIER,
  ONE_OF_FIELDS,
  SOURCE_HANDLE_IDENTIFIER,
  TARGET_HANDLE_IDENTIFIER,
  TIME_CONSTRAINT_HANDLE_END,
  TIME_CONSTRAINT_HANDLE_START,
} from '../constants';
import { DriverArgument, DriverArguments } from '../data/drivers';
import type {
  TaskDependenciesById,
  TimeConstraintInstancesById,
} from '../features/graph/graphSlice';
import type { LabwareByHandleIndexByTaskId } from '../features/selectors/types';
import type {
  LabwareError,
  MaybeWorkflowConfig,
  TaskError,
} from '../features/workflowJsonSelectors';
import { TimeConstraintError } from '../features/workflowJsonSelectors/timeConstraintErrors';
import {
  ConditionalTaskProperties,
  GraphEdges,
  InstrumentForLocation,
  LabwareClosureState,
  LabwareType,
  SelectionUpdate,
  TaskLabwareCorrespondence,
  TaskParameterValue,
  TaskProperties,
  TaskPropertiesForm,
  TaskType,
  TimeConstraintInstance,
} from '../types';
import { ConditionPropertiesForm } from '../zodSchemas/conditionProperties';

export function castDriverArgument(args: {
  data: DriverArgument;
  stringValue: string;
}): TaskParameterValue | null {
  const { data } = args;
  const trimmedStringValue = args.stringValue.trim();
  if (data.type === 'str') {
    return args.stringValue;
  }

  if (Array.isArray(data.type) && data.type[0] === 'str') {
    return trimmedStringValue
      .replaceAll(' ', '')
      .split(',')
      .filter((item) => item.replace(' ', '') !== '');
  }

  if (data.type === 'int' || data.type === 'float') {
    // We allow to enter any number. If it doesn't match the configuration, an error will be displayed.
    const n = Number(trimmedStringValue);
    return trimmedStringValue !== '' && Number.isFinite(n) ? n : null;
  }

  // TODO(IL, 2024-04-25): add "fileOption" type to drivers JSON
  // if (data.type === 'fileOption') {
  //   return data.options.includes(args.stringValue) ? args.stringValue : null;
  // }

  if (data.type === 'bool') {
    return args.stringValue === 'true';
  }

  logger.error(`Not implemented: driver argument with type: "${data.type}"`);
  return null;
}

/** Uses driver-defined types (in fake drivers) to cast task parameters from strings */
export function castTaskParameters(args: {
  driverArguments: DriverArguments;
  parameters: Record<string, string>;
}): Record<string, TaskParameterValue> {
  const { driverArguments, parameters } = args;
  return Object.entries(parameters).reduce<Record<string, TaskParameterValue>>(
    (acc, [parameterName, stringValue]) => {
      const data = driverArguments[parameterName];
      if (data === undefined) {
        logger.error(
          `no parameter named "${parameterName} in driverArguments"`
        );
        return acc;
      }

      const castValue = castDriverArgument({ data, stringValue });
      const isSpeedParameter =
        parameterName === 'speed' || parameterName === 'g_force';

      if (castValue === null && isSpeedParameter) {
        return { ...acc, [parameterName]: '' };
      }

      const isPartOfCollection = Object.values(ONE_OF_FIELDS).some((fields) =>
        fields.includes(parameterName)
      );
      if (castValue === null || (isPartOfCollection && castValue === '')) {
        return acc;
      }

      return { ...acc, [parameterName]: castValue };
    },
    {}
  );
}

export function taskPropertiesToForm(
  taskProperties: TaskProperties
): TaskPropertiesForm {
  const timeBreakdown = getTimeBreakdownStrings(
    taskProperties.userTimeEstimate
  );

  return {
    id: taskProperties.id,
    nickname: taskProperties.nickname ?? '',
    taskType: taskProperties.taskType,
    instrumentId: taskProperties.instrumentId ?? '',
    userTimeEstimateHours: timeBreakdown.hours,
    userTimeEstimateMinutes: timeBreakdown.minutes,
    userTimeEstimateSeconds: timeBreakdown.seconds,
    ...getFormModesFromProps(taskProperties),
  };
}

export function conditionPropertiesToForm(
  conditionProperties: ConditionalTaskProperties
): ConditionPropertiesForm {
  return {
    id: conditionProperties.id,
    elseLabel: conditionProperties.elseLabel || 'ELSE Condition',
    ifLabel: conditionProperties.ifLabel || 'IF Condition',
    propertiesType: conditionProperties.propertiesType,
    comparison: conditionProperties.comparison || {
      lhs: { key: '' },
      operator: '==',
      rhs: { value: '' },
    },
    expectedOutcome: conditionProperties.expectedOutcome ?? 'IF condition',
  };
}

export function getClosureStateOptionsForLabwareType(
  labwareType: LabwareType
): LabwareClosureState[] {
  if (labwareType === 'microplate') {
    return ['open', 'lidded', 'sealed'];
  }
  if (labwareType === 'reservoir') {
    return ['open', 'lidded'];
  }
  if (labwareType === 'tuberack') {
    return ['capped', 'uncapped'];
  }
  // other labware types have NO closure state options.
  return [];
}

export const getIsSingleLabwareTask = (taskType: string | undefined): boolean =>
  typeof taskType === 'string' &&
  ['dispose', 'store', 'incubate'].includes(taskType);

/** true if the task node is able to have more labware input handles added.
 * Incubation task node should only take one labware at a time.
 * This is due to the incubation time starting from once the first labware an entered the incubator,
 * meaning different labware will end up having different incubation times.
 * Store and Dispose tasks should also only take one labware at a time.
 */
export const getTaskCanAddMoreLabware = ({
  taskType,
  connectedLabwareCount,
  instrumentCapacity,
}: {
  taskType: string;
  connectedLabwareCount: number;
  instrumentCapacity: number;
}): boolean => {
  if (getIsSingleLabwareTask(taskType) && connectedLabwareCount === 1)
    return false;
  return connectedLabwareCount < instrumentCapacity;
};

/** true if the task node should have no output handles */
export function getTaskHasDeadEndNode(taskType: TaskType): boolean {
  // TODO(IL, 2024-03-27): this data should come from a central place.
  // Might need to be a selector on drivers, etc.
  return taskType === 'dispose' || taskType === 'store';
}

/** true if the instrument is a valid starting location, useful for filtering */
export function instrumentIsValidStartingLocation(
  instrument: InstrumentForLocation
): boolean {
  // TODO(IL, 2024-04-09): should this use info from the driver? If it's more than 'bin', consider
  // expanding drivers data. It might also be better as a Redux workflowSlice selector, etc.
  const blocklist = ['automata_bin'];
  return !blocklist.includes(instrument.driverName);
}

export function fillSparseArray<T>(arr: Array<T | undefined>): Array<T | null> {
  const newArray: Array<T | null> = [];
  for (let i = 0; i < arr.length; i++) {
    newArray.push(arr[i] ?? null);
  }
  return newArray;
}

/** merge arr1 on top of arr0, but ignore null or undefined entries in arr1. */
export function mergeNonNullsyInArray<T>(
  arr0: Array<T | null>,
  arr1: Array<T | null>
): Array<T | null> {
  const length = Math.max(arr0.length, arr1.length);
  const result: Array<T | null> = [];
  for (let i = 0; i < length; i++) {
    result.push(arr1[i] ?? arr0[i] ?? null);
  }
  return result;
}

export function labwareTypeToDisplayName(labwareType: LabwareType): string {
  switch (labwareType) {
    case 'microplate':
      return 'Plate';
    case 'reservoir':
      return 'Reservoir';
    case 'tuberack':
      return 'Tube Rack';
    case 'tiprack':
      return 'Tip Rack';
    default: {
      labwareType satisfies never;

      logger.error(`no display name for labwareType "${labwareType}"`);
      return '???';
    }
  }
}

function _isMacOS(): boolean {
  if ((navigator as any)?.userAgentData !== undefined) {
    // Navigator.userAgentData is experimental, only Chrome and Edge support it as of this writing
    // https://developer.mozilla.org/en-US/docs/Web/API/Navigator/userAgentData
    const platform = (navigator as any)?.userAgentData?.platform;
    if (typeof platform === 'string') {
      return /mac/i.test(platform);
    }
  }
  const parser = new UAParser('user-agent');
  const os = parser.getOS();
  return os.name === 'Mac OS';
}

// TODO(IL, 2024-04-09): use a library for more semantic memoization
let _GLOBAL_IS_MAC_OS: boolean | undefined;
/** Detect if platform is Mac with Navigator.userAgentData with fallback to ua-parser-js */
export function isMacOS(): boolean {
  if (_GLOBAL_IS_MAC_OS === undefined) {
    _GLOBAL_IS_MAC_OS = _isMacOS();
  }
  return _GLOBAL_IS_MAC_OS;
}

export function generateXyFlowSelectionUpdate(
  changes: NodeChange[] | EdgeChange[]
): SelectionUpdate | null {
  const selectionChanges = changes.flatMap((c) =>
    c.type === 'select' ? [c] : []
  );
  if (selectionChanges.length === 0) {
    return null;
  }

  return selectionChanges.reduce<SelectionUpdate>(
    (acc, change) => {
      if (change.selected) {
        return { ...acc, selected: [...acc.selected, change.id] };
      } else {
        return { ...acc, deselected: [...acc.deselected, change.id] };
      }
    },
    { selected: [], deselected: [] }
  );
}

export function mergeSelectionUpdate(
  incoming: SelectionUpdate,
  existing: string[]
): string[] {
  return uniq([
    ...existing.filter((x) => !incoming.deselected.includes(x)),
    ...incoming.selected,
  ]);
}

export function getConnectedEdgeIdsForNode(
  nodeId: string,
  edges: GraphEdges
): string[] {
  return getConnectedEdges(
    [{ id: nodeId, position: { x: 0, y: 0 }, data: {} }],
    edges
  ).map((edge) => edge.id);
}

export function getConnectedEdgeIdsForNodes(
  nodeIds: string[],
  edges: GraphEdges
): string[] {
  return union(
    ...nodeIds.map((nodeId) => getConnectedEdgeIdsForNode(nodeId, edges))
  );
}

export function getConnectedTaskDependencyIdsForNodes(
  taskIds: string[],
  taskDependenciesById: TaskDependenciesById
): string[] {
  return uniq(
    Object.values(taskDependenciesById).filter(
      ({ fromTaskId, toTaskId }) =>
        taskIds.includes(fromTaskId) || taskIds.includes(toTaskId)
    )
  ).map(({ id }) => id);
}

export function getConnectedTimeConstraintIdsForNodes(
  taskIds: string[],
  timeConstraintsById: TimeConstraintInstancesById
): string[] {
  return uniq(
    Object.values(timeConstraintsById)
      .filter(
        ({ source, target }) =>
          taskIds.includes(source) || taskIds.includes(target)
      )
      .map(({ id }) => id)
  );
}

export const workflowConfigHasWarning = (
  workflowJsonPreview: MaybeWorkflowConfig,
  warningMessage: LabwareError | TaskError | TimeConstraintError
) => {
  if (!workflowJsonPreview.isError) return false;
  return workflowJsonPreview.errors.some(
    ({ message }) => message === warningMessage
  );
};

const isTimeConstraintHandle = (handleId: string | null): boolean => {
  return (
    handleId === TIME_CONSTRAINT_HANDLE_START ||
    handleId === TIME_CONSTRAINT_HANDLE_END
  );
};

export const isTimeConstraintConnection = (cxn: Connection): boolean => {
  const isNotSameNode = cxn.source !== cxn.target;

  return (
    isNotSameNode && timeConstraintConnectionToPartialInstance(cxn) !== null
  );
};

export const timeConstraintConnectionToPartialInstance = (
  cxn: Connection
): Omit<
  TimeConstraintInstance,
  | 'id'
  | 'minDuration'
  | 'maxDuration'
  | 'enableDetailedCoverageCalculation'
  | 'autoIgnoreIfBroken'
> | null => {
  if (
    cxn.source !== null &&
    cxn.target !== null &&
    (cxn.sourceHandle === TIME_CONSTRAINT_HANDLE_START ||
      cxn.sourceHandle === TIME_CONSTRAINT_HANDLE_END) &&
    (cxn.targetHandle === TIME_CONSTRAINT_HANDLE_START ||
      cxn.targetHandle === TIME_CONSTRAINT_HANDLE_END)
  ) {
    return {
      source: cxn.source,
      sourceType: cxn.sourceHandle,
      target: cxn.target,
      targetType: cxn.targetHandle,
    };
  }
  return null;
};

export const getTaskLabwareCorrespondence = (
  nodeA: string,
  nodeB: string,
  labwareByHandleIndexByTaskId: LabwareByHandleIndexByTaskId
): TaskLabwareCorrespondence => {
  const labwareIdsA = Object.values(
    labwareByHandleIndexByTaskId[nodeA] ?? {}
  ).filter((value) => value !== null);
  const labwareIdsB = Object.values(
    labwareByHandleIndexByTaskId[nodeB] ?? {}
  ).filter((value) => value !== null);

  if (labwareIdsA.length === 0 && labwareIdsB.length === 0) {
    return 'NO_LABWARE_ASSIGNED';
  }

  const hasCommonLabware = intersection(labwareIdsA, labwareIdsB).length > 0;
  if (hasCommonLabware) {
    return 'COMMON_LABWARE';
  }

  return 'DIFFERENT_LABWARE';
};

export const isLabwareDependencyConnection = (cxn: Connection): boolean => {
  // if either handle is a time constraint handle, assume this is not
  // a valid labware dependency connection
  if (
    isTimeConstraintHandle(cxn.sourceHandle) ||
    isTimeConstraintHandle(cxn.targetHandle) ||
    cxn.sourceHandle?.includes(DATA_HANDLE_IDENTIFIER) ||
    cxn.targetHandle?.includes(DATA_HANDLE_IDENTIFIER)
  ) {
    return false;
  }

  const isNotSameNode = cxn.source !== cxn.target;

  let sourceHandle = cxn.sourceHandle;
  let targetHandle = cxn.targetHandle;

  // reverse if needed
  if (
    cxn.sourceHandle !== null &&
    parseHandlePairType(cxn.sourceHandle) === TARGET_HANDLE_IDENTIFIER
  ) {
    sourceHandle = cxn.targetHandle;
    targetHandle = cxn.sourceHandle;
  }

  const sourceHandleIsValid =
    sourceHandle === null ||
    parseHandlePairType(sourceHandle) === SOURCE_HANDLE_IDENTIFIER;

  const targetHandleIsValid =
    targetHandle !== null &&
    parseHandlePairType(targetHandle) === TARGET_HANDLE_IDENTIFIER;

  return isNotSameNode && sourceHandleIsValid && targetHandleIsValid;
};

export const getVersionedDriverName = (input: {
  name: string;
  version: string;
}): string => `${input.name}_${input.version}`;

export const isDataConnection = (cxn: Connection): boolean => {
  return (
    (cxn.sourceHandle?.includes(DATA_HANDLE_IDENTIFIER) &&
      cxn.targetHandle?.includes(DATA_HANDLE_IDENTIFIER)) ??
    false
  );
};

export const HAMILTON_DRIVER_NAME = 'hamilton_starlet';

const NO_LABWARE_TASKS = ['backflush_by_time', 'backflush', 'prime'];

export const isNoLabwareEnabled = (taskType: string): boolean => {
  const noLabwareTasks = new Set(NO_LABWARE_TASKS);
  return noLabwareTasks.has(taskType);
};

export const START_OR_STOP_SHAKE_TASKS = ['start_shake', 'stop_shake'];

export const isStartOrStopShakeTask = (taskType: string): boolean => {
  const shakeTasks = new Set(START_OR_STOP_SHAKE_TASKS);
  return shakeTasks.has(taskType);
};

export const disableInstruments = (
  instruments: Record<string, object>,
  instrumentStates: Record<string, boolean>
): Record<string, any> => {
  // Filter out disabled instruments
  return Object.keys(instruments).reduce((result, instrumentKey) => {
    if (
      instrumentStates[instrumentKey] === undefined ||
      instrumentStates[instrumentKey] === true
    ) {
      result[instrumentKey] = instruments[instrumentKey];
    }
    return result;
  }, {} as Record<string, any>);
};

export const renameInstruments = (
  instruments: Record<string, object>,
  instrumentRenames: Record<string, string>
) => {
  return Object.keys(instruments).reduce((result, instrumentKey) => {
    const newKey = instrumentRenames[instrumentKey] || instrumentKey;
    result[newKey] = instruments[instrumentKey];
    return result;
  }, {} as Record<string, any>);
};

export const disableInstrumentConfigOptions = (
  instrumentConfigs: Record<string, { config: Record<string, any> }>,
  instrumentConfigStates: Record<string, Record<string, boolean>>
): Record<string, any> => {
  // Filter out disabled instrument config options
  return Object.keys(instrumentConfigs).reduce((result, instrumentKey) => {
    const configOptions = instrumentConfigs[instrumentKey]?.config || {};
    const configStates = instrumentConfigStates[instrumentKey] || {};

    const filteredConfigOptions = Object.keys(configOptions || {}).reduce(
      (configResult, optionKey) => {
        if (
          configStates[optionKey] === undefined ||
          configStates[optionKey] === true
        ) {
          configResult[optionKey] = configOptions[optionKey];
        }
        return configResult;
      },
      {} as Record<string, any>
    );

    result[instrumentKey] = {
      ...instrumentConfigs[instrumentKey],
      config: filteredConfigOptions,
    };
    return result;
  }, {} as Record<string, any>);
};

export const prepareInstrumentsForWorkflowConfig = (
  instruments: Record<string, object>,
  instrumentStates: Record<string, boolean>,
  instrumentRenames: Record<string, string>,
  instrumentConfigStates: Record<string, Record<string, boolean>>
) => {
  const filteredInstruments = disableInstruments(instruments, instrumentStates);
  const renamedInstruments = renameInstruments(
    filteredInstruments,
    instrumentRenames
  );

  const instrumentsWithDisabledConfigOptions = disableInstrumentConfigOptions(
    renamedInstruments,
    instrumentConfigStates
  );

  return instrumentsWithDisabledConfigOptions;
};
